<template>
  <div class="space-y-6">
    <div class="mb-6">
      <h2 class="text-2xl font-bold text-gray-900 dark:text-white mb-2">Admin-Einstellungen</h2>
      <p class="text-gray-600 dark:text-gray-400">
        Verwalten Sie die Einstellungen für Admin-Benachrichtigungen.
      </p>
    </div>

    <!-- Loading State -->
    <div v-if="loading" class="flex justify-center items-center py-12">
      <div class="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 dark:border-blue-400"></div>
    </div>

    <!-- Formular -->
    <form v-else @submit.prevent="handleSubmit" class="space-y-8">
      <!-- Benachrichtigungs-E-Mail -->
      <div class="bg-gray-50 dark:bg-gray-900/50 rounded-lg p-6 border border-gray-200 dark:border-gray-700">
        <h3 class="text-lg font-semibold text-gray-900 dark:text-white mb-4">Benachrichtigungs-E-Mail</h3>
        <div class="space-y-4">
          <div>
            <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              E-Mail-Adresse für Kontaktformular-Benachrichtigungen
            </label>
            <input
              v-model="formData.notificationEmail"
              type="email"
              class="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 focus:border-blue-500 dark:focus:border-blue-400 transition-colors"
              placeholder="admin@example.com"
            />
            <p class="mt-2 text-sm text-gray-600 dark:text-gray-400">
              Diese E-Mail-Adresse erhält eine Benachrichtigung, wenn über das Kontaktformular eine neue Nachricht gesendet wurde.
              <br>
              <span class="text-gray-500 dark:text-gray-500">
                Wenn keine E-Mail-Adresse hinterlegt ist, wird die Standard-E-Mail-Adresse aus der Cloud Function Config verwendet.
              </span>
            </p>
          </div>
        </div>
      </div>

      <!-- Submit Button -->
      <div class="flex justify-end gap-4">
        <button
          type="button"
          @click="resetForm"
          class="px-6 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
          :disabled="submitting"
        >
          Zurücksetzen
        </button>
        <button
          type="submit"
          class="px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 dark:hover:bg-blue-500 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          :disabled="submitting"
        >
          <span v-if="submitting" class="flex items-center gap-2">
            <svg class="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
              <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
            Speichern...
          </span>
          <span v-else>Speichern</span>
        </button>
      </div>

      <!-- Success Message -->
      <div
        v-if="successMessage"
        class="p-4 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-md text-green-800 dark:text-green-200"
      >
        {{ successMessage }}
      </div>

      <!-- Error Message -->
      <div
        v-if="errorMessage"
        class="p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-md text-red-800 dark:text-red-200"
      >
        {{ errorMessage }}
      </div>
    </form>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted } from 'vue'
import { getAdminSettings, updateAdminSettings, getDefaultAdminSettings, type AdminSettings } from '../../firebase/adminSettings'
import { useErrorHandler } from '../../composables/useErrorHandler'

const loading = ref(true)
const submitting = ref(false)
const { handleError } = useErrorHandler()
const successMessage = ref('')
const errorMessage = ref('')

const formData = ref<Omit<AdminSettings, 'updatedAt' | 'updatedBy'>>({
  notificationEmail: undefined
})

const originalData = ref<Omit<AdminSettings, 'updatedAt' | 'updatedBy'> | null>(null)

const loadAdminSettings = async () => {
  try {
    loading.value = true
    const adminSettings = await getAdminSettings()
    
    if (adminSettings) {
      formData.value = {
        notificationEmail: adminSettings.notificationEmail || undefined
      }
      originalData.value = { ...formData.value }
    } else {
      // Fallback zu Standard-Werten
      const defaultData = getDefaultAdminSettings()
      formData.value = {
        notificationEmail: defaultData.notificationEmail || undefined
      }
      originalData.value = { ...formData.value }
    }
  } catch (error) {
    const appError = await handleError(error, 'Fehler beim Laden der Admin-Einstellungen')
    errorMessage.value = appError.userMessage
  } finally {
    loading.value = false
  }
}

const handleSubmit = async () => {
  try {
    submitting.value = true
    successMessage.value = ''
    errorMessage.value = ''

    // Validiere E-Mail-Adresse falls angegeben
    if (formData.value.notificationEmail && formData.value.notificationEmail.trim()) {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
      if (!emailRegex.test(formData.value.notificationEmail.trim())) {
        errorMessage.value = 'Bitte geben Sie eine gültige E-Mail-Adresse ein.'
        return
      }
    }

    const dataToSave: Partial<AdminSettings> = {}
    
    // E-Mail-Adresse nur hinzufügen wenn nicht leer
    if (formData.value.notificationEmail?.trim()) {
      dataToSave.notificationEmail = formData.value.notificationEmail.trim()
    } else {
      // Wenn leer, setze auf undefined um es zu löschen
      dataToSave.notificationEmail = undefined
    }

    await updateAdminSettings(dataToSave)
    
    // Lade aktualisierte Daten
    await loadAdminSettings()
    
    successMessage.value = 'Admin-Einstellungen erfolgreich gespeichert!'
    setTimeout(() => {
      successMessage.value = ''
    }, 5000)
  } catch (error) {
    const appError = await handleError(error, 'Fehler beim Speichern der Admin-Einstellungen')
    errorMessage.value = appError.userMessage
  } finally {
    submitting.value = false
  }
}

const resetForm = () => {
  if (originalData.value) {
    formData.value = { ...originalData.value }
  }
  successMessage.value = ''
  errorMessage.value = ''
}

onMounted(() => {
  loadAdminSettings()
})
</script>









